/**
 * StepUpDialog — "Confirm it's you" (two-step verification, 2026-09-26).
 *
 * Asks for a SECOND sign-in method inside a session that is already signed in. The choices come from
 * the server (`stepUpOptions` = the methods this person has set up here, minus the ones already proven
 * this session, plus "recovery" when codes remain). Each choice runs its own step-up mutation; on
 * success the RE-ISSUED token is adopted (AuthContext `adoptToken`) before `onVerified` fires, so the
 * next request already carries the aal2 session.
 *
 * Usually opened through `useStepUp()` rather than directly.
 */
import React from "react";
import {
    Alert, AlertDescription, AlertIcon, Box, Button, FormControl, FormLabel, Heading, Icon, Input, Link, Modal, ModalBody,
    ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, VStack,
} from "@chakra-ui/react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import { FiKey, FiLock, FiMail, FiMessageSquare, FiShield, FiChevronRight } from "react-icons/fi";
import { IconType } from "react-icons";
import { useAuth } from "../../../contexts/AuthContext";
import {
    STEP_UP_OPTIONS, STEP_UP_WITH_PASSWORD, STEP_UP_PASSKEY_OPTIONS, STEP_UP_WITH_PASSKEY, SEND_STEP_UP_CODE,
    STEP_UP_WITH_CODE, STEP_UP_WITH_RECOVERY_CODE,
} from "../queries";
import { browserSupportsWebAuthn, friendlyPasskeyError, stepUpWithPasskeyCeremony } from "../utils/passkey";
import { errorWords, isStepUpFailedError } from "../utils/assurance";
import type { StepUpResult } from "../../../generated/graphql";
import { AuthTip, AuthLoading, AuthError, alpha, useAuthTheme } from "./ui";

export interface StepUpDialogProps {
    isOpen: boolean;
    /** Plain words: why we are asking right now (shown under the heading). */
    reason?: string;
    /** The person proved a second method; the new token is already stored. */
    onVerified: (result: StepUpResult) => void;
    /** Closed without proving anything. */
    onCancel: () => void;
}

type Channel = "email" | "sms";
type Step = { kind: "choose" } | { kind: "password" } | { kind: "code"; channel: Channel; sent: boolean } | { kind: "recovery" };

/** The words and icon for each choice, in the order they are offered. */
export const STEP_UP_CHOICES: { type: string; label: string; hint: string; icon: IconType }[] = [
    { type: "passkey", label: "Use your passkey", hint: "Touch ID, Face ID, Windows Hello or a security key", icon: FiKey },
    { type: "pwd", label: "Enter your password", hint: "The password you sign in with", icon: FiLock },
    { type: "email_code", label: "Email me a code", hint: "We send a code to your email address", icon: FiMail },
    { type: "sms_code", label: "Text me a code", hint: "We text a code to your mobile", icon: FiMessageSquare },
    { type: "recovery", label: "Use a recovery code", hint: "One of the codes you saved when you turned this on", icon: FiShield },
];

/** The server's options → the choices to show (known types only, in our order). Pure, unit-tested. */
export function stepUpChoicesFor(options: readonly string[] | null | undefined, passkeysSupported = true) {
    const set = new Set(options || []);
    return STEP_UP_CHOICES.filter((c) => set.has(c.type) && (c.type !== "passkey" || passkeysSupported));
}

// Field components live at module scope so a phone keyboard never closes on a keystroke.
const SecretField: React.FC<{
    id: string; label: string; value: string; onChange: (v: string) => void; onEnter: () => void;
    type?: string; inputMode?: "numeric" | "text"; autoComplete?: string; help?: string;
}> = ({ id, label, value, onChange, onEnter, type = "text", inputMode = "text", autoComplete, help }) => {
    const t = useAuthTheme();
    return (
        <FormControl>
            <FormLabel htmlFor={id} fontSize="md" fontWeight="semibold" color={t.text}>{label}</FormLabel>
            <Input
                id={id}
                size="lg"
                autoFocus
                type={type}
                inputMode={inputMode}
                autoComplete={autoComplete}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnter(); } }}
            />
            {help && <Text mt={2} fontSize="sm" color={t.faint}>{help}</Text>}
        </FormControl>
    );
};

export const StepUpDialog: React.FC<StepUpDialogProps> = ({ isOpen, reason, onVerified, onCancel }) => {
    const t = useAuthTheme();
    const auth = useAuth();
    const [step, setStep] = React.useState<Step>({ kind: "choose" });
    const [value, setValue] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const [problem, setProblem] = React.useState<string | null>(null);
    // Progress, not a problem: shown after the first of two proofs.
    const [progress, setProgress] = React.useState<string | null>(null);

    const [loadOptions, { data, loading, error, refetch }] = useLazyQuery<{ stepUpOptions: string[] }>(STEP_UP_OPTIONS, { fetchPolicy: "network-only" });
    const [withPassword] = useMutation(STEP_UP_WITH_PASSWORD);
    const [passkeyOptions] = useMutation(STEP_UP_PASSKEY_OPTIONS);
    const [withPasskey] = useMutation(STEP_UP_WITH_PASSKEY);
    const [sendCode] = useMutation(SEND_STEP_UP_CODE);
    const [withCode] = useMutation(STEP_UP_WITH_CODE);
    const [withRecovery] = useMutation(STEP_UP_WITH_RECOVERY_CODE);

    // Fresh options every time it opens: what was proven a minute ago is no longer offered.
    React.useEffect(() => {
        if (!isOpen) return;
        setStep({ kind: "choose" });
        setValue("");
        setProblem(null);
        setProgress(null);
        void loadOptions();
    }, [isOpen, loadOptions]);

    const choices = stepUpChoicesFor(data?.stepUpOptions, browserSupportsWebAuthn());

    const adopt = async (result: StepUpResult | undefined | null) => {
        if (!result?.token) throw new Error("The server did not confirm it. Please try again.");
        if (auth.adoptToken) await auth.adoptToken(result.token);
        else { localStorage.setItem("auth_token", result.token); await auth.refreshAuth?.(); }
        // A session from before 2026-09-26 carries no proven factor, so one proof lifts it to only ONE kind
        // (the server says aal 1). Stay open and ask for a different kind until the server says 2.
        if ((result.aal ?? 2) < 2) {
            setStep({ kind: "choose" });
            setValue("");
            setProblem(null);
            setProgress("That worked. One more step: confirm with a different way of signing in.");
            await loadOptions();
            return;
        }
        onVerified(result);
    };

    const fail = (err: any, fallback?: string) => {
        setProblem(errorWords(err, fallback));
        if (isStepUpFailedError(err)) setValue("");
    };

    const runPasskey = async () => {
        setBusy(true); setProblem(null);
        try {
            await adopt(await stepUpWithPasskeyCeremony(passkeyOptions, withPasskey));
        } catch (err: any) {
            setProblem(err?.graphQLErrors?.length ? errorWords(err) : friendlyPasskeyError(err));
        } finally { setBusy(false); }
    };

    const send = async (channel: Channel) => {
        setBusy(true); setProblem(null);
        try {
            const res = await sendCode({ variables: { channel } });
            if (!res.data?.sendStepUpCode) throw new Error("The code could not be sent. Please try again.");
            setStep({ kind: "code", channel, sent: true });
            setValue("");
        } catch (err) {
            fail(err, "The code could not be sent. Please try again.");
        } finally { setBusy(false); }
    };

    const choose = (type: string) => {
        setProblem(null); setValue("");
        if (type === "passkey") void runPasskey();
        else if (type === "pwd") setStep({ kind: "password" });
        else if (type === "email_code") { setStep({ kind: "code", channel: "email", sent: false }); void send("email"); }
        else if (type === "sms_code") { setStep({ kind: "code", channel: "sms", sent: false }); void send("sms"); }
        else if (type === "recovery") setStep({ kind: "recovery" });
    };

    const submit = async () => {
        const typed = value.trim();
        if (!typed || busy) return;
        setBusy(true); setProblem(null);
        try {
            if (step.kind === "password") {
                const res = await withPassword({ variables: { password: value } });
                await adopt(res.data?.stepUpWithPassword);
            } else if (step.kind === "code") {
                const res = await withCode({ variables: { channel: step.channel, code: typed.replace(/\s+/g, "") } });
                await adopt(res.data?.stepUpWithCode);
            } else if (step.kind === "recovery") {
                const res = await withRecovery({ variables: { code: typed } });
                await adopt(res.data?.stepUpWithRecoveryCode);
            }
        } catch (err) {
            fail(err);
        } finally { setBusy(false); }
    };

    const back = () => { setStep({ kind: "choose" }); setValue(""); setProblem(null); };

    const body = (() => {
        if (loading && !data) return <AuthLoading label="Finding the ways you can confirm it's you" cards={1} columns={{ base: 1 }} />;
        if (error) return <AuthError title="Couldn't load your sign-in methods" error={error} onRetry={() => refetch()} />;
        if (step.kind === "choose") {
            if (choices.length === 0) {
                return (
                    <Alert status="info" borderRadius="lg" data-testid="step-up-no-options">
                        <AlertIcon />
                        <AlertDescription fontSize="md">
                            There's no second sign-in method you can use here yet. Add one — for example a passkey or a
                            password — on your{" "}
                            <Link as={RouterLink} to="/security" textDecoration="underline" onClick={onCancel}>Security page</Link>, then try again.
                        </AlertDescription>
                    </Alert>
                );
            }
            return (
                <VStack align="stretch" spacing={3} data-testid="step-up-choices">
                    {choices.map((c) => (
                        <AuthTip key={c.type} label={`${c.hint}. Nothing on your account changes — this only confirms it's you.`}>
                            <Button
                                w="100%"
                                h="auto"
                                minH="56px"
                                py={3}
                                px={4}
                                variant="outline"
                                justifyContent="flex-start"
                                textAlign="left"
                                whiteSpace="normal"
                                isDisabled={busy}
                                isLoading={busy && c.type === "passkey"}
                                loadingText="Waiting for your passkey…"
                                onClick={() => choose(c.type)}
                                leftIcon={<Icon as={c.icon} boxSize={5} color={t.primary} />}
                                rightIcon={<Icon as={FiChevronRight} />}
                            >
                                <Box flex="1" minW={0}>
                                    <Text fontSize="md" fontWeight="semibold" color={t.text}>{c.label}</Text>
                                    <Text fontSize="sm" fontWeight="normal" color={t.muted}>{c.hint}</Text>
                                </Box>
                            </Button>
                        </AuthTip>
                    ))}
                </VStack>
            );
        }
        if (step.kind === "password") {
            return <SecretField id="step-up-password" label="Your password" type="password" autoComplete="current-password" value={value} onChange={setValue} onEnter={submit} />;
        }
        if (step.kind === "code") {
            const where = step.channel === "email" ? "email" : "text message";
            return (
                <Stack spacing={3}>
                    <Text fontSize="md" color={t.muted}>
                        {step.sent ? `We sent a code by ${where}. Type it here.` : busy ? `Sending a code by ${where}…` : `No code was sent yet. Press "Send a new code" to try again.`}
                    </Text>
                    <SecretField
                        id="step-up-code"
                        label={step.channel === "email" ? "Code from the email" : "Code from the text message"}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={value}
                        onChange={setValue}
                        onEnter={submit}
                        help="Codes work once. After three wrong tries you'll need to wait 15 minutes."
                    />
                    <AuthTip label={`Send a new code by ${where}. The old code stops working.`}>
                        <Button variant="link" alignSelf="start" minH="44px" isDisabled={busy} onClick={() => void send(step.channel)}>
                            Send a new code
                        </Button>
                    </AuthTip>
                </Stack>
            );
        }
        return (
            <SecretField
                id="step-up-recovery"
                label="A recovery code"
                autoComplete="off"
                value={value}
                onChange={setValue}
                onEnter={submit}
                help="Each recovery code works once. After you use it, it's crossed off."
            />
        );
    })();

    const typing = step.kind !== "choose";

    return (
        <Modal isOpen={isOpen} onClose={onCancel} size={{ base: "full", sm: "md" }} isCentered closeOnOverlayClick={!busy}>
            <ModalOverlay />
            <ModalContent mx={{ base: 0, sm: 3 }} maxH={{ sm: "calc(100dvh - 48px)" }} overflowY="auto" data-testid="step-up-dialog">
                <ModalHeader pr={12}>
                    <Heading as="h2" size="md" color={t.text}>Confirm it's you</Heading>
                    <Text mt={1} fontSize="md" fontWeight="normal" color={t.muted}>
                        {reason || "Use a second sign-in method to confirm it's you."}
                    </Text>
                </ModalHeader>
                <ModalCloseButton minW="44px" minH="44px" isDisabled={busy} />
                <ModalBody>
                    <Stack spacing={4}>
                        {progress && (
                            <Box role="status" p={3} borderRadius="lg" bg={alpha(t.success, 0.1)} borderWidth="1px" borderColor={alpha(t.success, 0.35)}>
                                <Text fontSize="md" color={t.text}>{progress}</Text>
                            </Box>
                        )}
                        {body}
                        {problem && (
                            <Box role="alert" p={3} borderRadius="lg" bg={alpha(t.danger, 0.08)} borderWidth="1px" borderColor={alpha(t.danger, 0.3)}>
                                <Text fontSize="md" color={t.text}>{problem}</Text>
                            </Box>
                        )}
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Stack direction={{ base: "column-reverse", sm: "row" }} spacing={3} w={{ base: "full", sm: "auto" }}>
                        {typing ? (
                            <AuthTip label="Pick a different way to confirm it's you. Nothing is sent or changed.">
                                <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} variant="outline" isDisabled={busy} onClick={back}>
                                    Use another way
                                </Button>
                            </AuthTip>
                        ) : (
                            <AuthTip label="Close without confirming. Whatever you were doing is not done.">
                                <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} variant="outline" isDisabled={busy} onClick={onCancel}>
                                    Cancel
                                </Button>
                            </AuthTip>
                        )}
                        {typing && (
                            <AuthTip label="Check what you typed with the server. If it's right, you're confirmed for the next few minutes.">
                                <Button
                                    size="lg"
                                    minH="48px"
                                    w={{ base: "full", sm: "auto" }}
                                    colorScheme="blue"
                                    isLoading={busy}
                                    loadingText="Checking…"
                                    isDisabled={!value.trim()}
                                    onClick={() => void submit()}
                                >
                                    Confirm
                                </Button>
                            </AuthTip>
                        )}
                    </Stack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default StepUpDialog;
